import subMonths from "date-fns/subMonths";
import startOfWeek from "date-fns/startOfWeek";
import startOfMonth from "date-fns/startOfMonth";
import startOfDay from "date-fns/startOfDay";
import isSameDay from "date-fns/isSameDay";
import endOfWeek from "date-fns/endOfWeek";
import endOfMonth from "date-fns/endOfMonth";
import endOfDay from "date-fns/endOfDay";
import eachDayOfInterval from "date-fns/eachDayOfInterval";
import addMonths from "date-fns/addMonths";
import addDays from "date-fns/addDays";
import { IAgendaEventType, IIssuerType } from "types/graphqlTypes";
import { EventsCalendarFilterRange, EventsCalenderFilterEventType } from "./types";

export function createViewOfMonthDays(date: Date, showOutsideDays = true) {
  const currentDate = new Date();

  const minDayPossible = addMonths(currentDate, -6);
  const maxDayPossible = addMonths(currentDate, 6);

  const firstDayOfMonth = startOfMonth(date);
  const endDayOfMonth = endOfMonth(date);
  const endDayOfWeek = endOfWeek(currentDate, {
    weekStartsOn: 1,
  });

  const currentWeek = eachDayOfInterval({
    start: startOfWeek(currentDate, {
      weekStartsOn: 1,
    }),
    end: endDayOfWeek,
  });

  endDayOfWeek.setDate(endDayOfWeek.getDate() + 1);
  const nextWeek = eachDayOfInterval({
    start: startOfWeek(endDayOfWeek, {
      weekStartsOn: 1,
    }),
    end: endOfWeek(endDayOfWeek, {
      weekStartsOn: 1,
    }),
  });

  if (showOutsideDays) {
    endDayOfMonth.setHours(0, 0, 0, 0);

    if (firstDayOfMonth.getDay() === 0) {
      firstDayOfMonth.setDate(firstDayOfMonth.getDate() - 6);
    } else if (firstDayOfMonth.getDay() !== 1) {
      firstDayOfMonth.setDate(firstDayOfMonth.getDate() - firstDayOfMonth.getDay() + 1);
    }

    const numberOfDays = eachDayOfInterval({
      start: firstDayOfMonth,
      end: endDayOfMonth,
    }).length;
    const numberOfDaysInSixWeek = 42;

    if (numberOfDays < numberOfDaysInSixWeek)
      endDayOfMonth.setDate(endDayOfMonth.getDate() + (numberOfDaysInSixWeek - numberOfDays));
  }

  const days = eachDayOfInterval({
    start: firstDayOfMonth,
    end: endDayOfMonth,
  }).map((day) => {
    const isOutsideDay = day.getMonth() !== date.getMonth();
    const isOutsideDayOfLimitRange = day < minDayPossible || day > maxDayPossible;

    return {
      date: day,
      isOutsideDay,
      isOutsideDayOfLimitRange,
      isCurrentWeek: !!currentWeek.find((date) => date.getTime() === day.getTime()),
      isNextWeek: !!nextWeek.find((date) => date.getTime() === day.getTime()),
      isToday: isSameDay(day, currentDate),
    };
  });

  return {
    minDayPossible,
    maxDayPossible,
    days,
  };
}

export function adjustToNextMonth(date: Date) {
  return addMonths(date, 1);
}

export function adjustToPreviousMonth(date: Date) {
  return addMonths(date, -1);
}

export function getEventNameWithEventType(code: IAgendaEventType, year?: number | null, locale: "USA" | "BRA" = "BRA") {
  const twoDigitYear = year?.toString().slice(-2);

  switch (code) {
    case IAgendaEventType.GeneralMeeting:
      return locale === "BRA" ? "Reunião Geral" : "General Meeting";

    case IAgendaEventType.PaymentOfCashDividends:
    case IAgendaEventType.PaymentOfSpecialCashDividends:
      return locale === "BRA" ? "Dividendos" : "Dividends";

    case IAgendaEventType.PaymentOfStockDividends:
      return locale === "BRA" ? "Bonificação" : "Stock Dividends";

    case IAgendaEventType.FirstQuarterEarningsResults:
      return locale === "BRA" ? `Balanço do 1T${twoDigitYear}` : `Earnings of 1Q${twoDigitYear}`;

    case IAgendaEventType.SecondQuarterEarningsResults:
      return locale === "BRA" ? `Balanço do 2T${twoDigitYear}` : `Earnings of 2Q${twoDigitYear}`;

    case IAgendaEventType.ThirdQuarterEarningsResults:
      return locale === "BRA" ? `Balanço do 3T${twoDigitYear}` : `Earnings of 3Q${twoDigitYear}`;

    case IAgendaEventType.FourthQuarterEarningsResults:
      return locale === "BRA" ? `Balanço do 4T${twoDigitYear}` : `Earnings of 4Q${twoDigitYear}`;

    case IAgendaEventType.FirstQuarterEarningsConferenceCall:
      return locale === "BRA" ? `Conferência do 1T${twoDigitYear}` : `Conference of 1Q${twoDigitYear}`;

    case IAgendaEventType.SecondQuarterEarningsConferenceCall:
      return locale === "BRA" ? `Conferência do 2T${twoDigitYear}` : `Conference of 2Q${twoDigitYear}`;

    case IAgendaEventType.ThirdQuarterEarningsConferenceCall:
      return locale === "BRA" ? `Conferência do 3T${twoDigitYear}` : `Conference of 3Q${twoDigitYear}`;

    case IAgendaEventType.FourthQuarterEarningsConferenceCall:
      return locale === "BRA" ? `Conferência do 4T${twoDigitYear}` : `Conference of 4Q${twoDigitYear}`;

    case IAgendaEventType.AnnualReport:
      return locale === "BRA" ? "Relatório Anual" : "Annual Report";

    case IAgendaEventType.StockSplit:
      return locale === "BRA" ? "Desdobramento" : "Spin Split";

    case IAgendaEventType.RightsIssue:
      return locale === "BRA" ? "Subscrição" : "Rights Issue";

    case IAgendaEventType.SpinOff:
      return locale === "BRA" ? "Cisão" : "Spin Off";

    default:
      throw new Error(`Unknown event code: ${code}`);
  }
}

export function getAgendaIssuerType(type: string): IIssuerType | undefined {
  switch (type) {
    case IIssuerType.BrazilianCompany:
    case IIssuerType.AmericanCompany:
    case IIssuerType.AmericanReit:
      return type;

    default:
      return undefined;
  }
}

export function getAgendaEventType(eventType: EventsCalenderFilterEventType) {
  switch (eventType) {
    case EventsCalenderFilterEventType.Earnings:
      return [
        IAgendaEventType.FirstQuarterEarningsResults,
        IAgendaEventType.SecondQuarterEarningsResults,
        IAgendaEventType.ThirdQuarterEarningsResults,
        IAgendaEventType.FourthQuarterEarningsResults,
      ];

    case EventsCalenderFilterEventType.Dividends:
      return [IAgendaEventType.PaymentOfCashDividends, IAgendaEventType.PaymentOfSpecialCashDividends];

    case EventsCalenderFilterEventType.StockDividends:
      return [IAgendaEventType.PaymentOfStockDividends];

    case EventsCalenderFilterEventType.RightsIssue:
      return [IAgendaEventType.RightsIssue];

    case EventsCalenderFilterEventType.StockSplit:
      return [IAgendaEventType.StockSplit];

    case EventsCalenderFilterEventType.SpinOff:
      return [IAgendaEventType.SpinOff];

    default:
      return null;
  }
}

export function getFilterValuesBySelectOption(code: EventsCalendarFilterRange) {
  const currentDate = new Date();
  const options = {
    weekStartsOn: 1 as 0 | 2 | 1 | 3 | 4 | 5 | 6 | undefined,
  };

  const {
    firstDayOfTheWeek,
    lastDayOfTheWeek,
    firstDayOfTheMonth,
    lastDayOfTheMonth,
    firstDayOfTheLastMonth,
    lastDayOfTheLastMonth,
    firstDayOfTheNextMonth,
    lastDayOfTheNextMonth,
    lastDayOfTheNextNinetyDays,
    firstDayOfTheYear,
    lastDayOfTheYear,
  } = {
    firstDayOfTheWeek: startOfWeek(currentDate, options),
    lastDayOfTheWeek: endOfWeek(currentDate, options),
    firstDayOfTheMonth: startOfMonth(currentDate),
    lastDayOfTheMonth: endOfMonth(currentDate),
    firstDayOfTheLastMonth: startOfMonth(subMonths(currentDate, 1)),
    lastDayOfTheLastMonth: endOfMonth(subMonths(currentDate, 1)),
    firstDayOfTheNextMonth: startOfMonth(addMonths(currentDate, 1)),
    lastDayOfTheNextMonth: endOfMonth(addMonths(currentDate, 1)),
    lastDayOfTheNextNinetyDays: endOfMonth(addDays(currentDate, 90)),
    firstDayOfTheYear: subMonths(currentDate, 6),
    lastDayOfTheYear: addMonths(currentDate, 6),
  };

  switch (code) {
    case EventsCalendarFilterRange.Today:
      return {
        filterType: EventsCalendarFilterRange.Today,
        startDate: startOfDay(currentDate),
        endDate: endOfDay(currentDate),
      };

    case EventsCalendarFilterRange.InTheWeek:
      return {
        filterType: EventsCalendarFilterRange.InTheWeek,
        startDate: startOfDay(firstDayOfTheWeek),
        endDate: endOfDay(lastDayOfTheWeek),
      };

    case EventsCalendarFilterRange.InTheMonth:
      return {
        filterType: EventsCalendarFilterRange.InTheMonth,
        startDate: startOfDay(firstDayOfTheMonth),
        endDate: endOfDay(lastDayOfTheMonth),
      };

    case EventsCalendarFilterRange.InTheLastMonth:
      return {
        filterType: EventsCalendarFilterRange.InTheLastMonth,
        startDate: startOfDay(firstDayOfTheLastMonth),
        endDate: endOfDay(lastDayOfTheLastMonth),
      };

    case EventsCalendarFilterRange.InTheNextMonth:
      return {
        filterType: EventsCalendarFilterRange.InTheNextMonth,
        startDate: startOfDay(firstDayOfTheNextMonth),
        endDate: endOfDay(lastDayOfTheNextMonth),
      };

    case EventsCalendarFilterRange.InTheNextNinetyDays:
      return {
        filterType: EventsCalendarFilterRange.InTheNextNinetyDays,
        startDate: startOfDay(currentDate),
        endDate: endOfDay(lastDayOfTheNextNinetyDays),
      };

    case EventsCalendarFilterRange.InTheYear:
      return {
        filterType: EventsCalendarFilterRange.InTheYear,
        startDate: startOfDay(firstDayOfTheYear),
        endDate: endOfDay(lastDayOfTheYear),
      };

    default:
      throw new Error(`Unknown filter date code: ${code}`);
  }
}
